"use client";

import React, { useMemo } from "react";
import Link from "@/components/Link";
import {
  Button,
  Anchor as MantineAnchor,
  Box as MantineBox,
  Image as MantineImage,
  Text as MantineText,
  Title as MantineTitle,
} from "@mantine/core";

import { useGlobalData } from "@/context/globalContext";
import { sendGtagEvent } from "@/lib/client/gtag";
import { trackEvent } from "@/lib/client/analytics";
import { trackItemOutboundClick } from "@/lib/client/itemTracking";
import { formatDateInTimezone, getItemTimestampDisplay } from "@/lib/client/utils";
import CouponIcon from "@/icons/Coupon";
import AuctionIcon from "@/icons/Auction";
import NewWindowIcon from "@/icons/NewWindow";

import styles from "./styles.module.scss";

type Props = {
  item: SearchResultItem;
  removeSelection: (id: number) => void;
};

const SelectionCard = ({ item, removeSelection }: Props) => {
  const { proxies } = useGlobalData();
  const isSold = item?.isSold;
  const filteredProxies = useMemo(() => {
    return proxies?.filter((proxy) => proxy.countryname === item.region)[0]?.proxies;
  }, [item.region, proxies]);
  const timestampDisplay = getItemTimestampDisplay({
    item: item as SearchResultItem,
    forceAdded: true,
  });

  return (
    <MantineBox className={styles.itemCard} data-sold={isSold}>
      <Button
        className={styles.removeButton}
        onClick={() => removeSelection(item.id)}
        variant="outline"
      >
        REMOVE
      </Button>
      <MantineImage
        src={item.imageurl}
        alt={item.title}
        loading="lazy"
        onError={(e) => {
          e.currentTarget.src = "/kurb_broken_image.svg";
          e.currentTarget.dataset.broken = "true";
        }}
      />
      <MantineBox className={styles.body}>
        <MantineBox className={styles.text}>
          <MantineBox className={styles.brandBar}>
            <MantineTitle order={4}>{item.brand?.label}</MantineTitle>
            <MantineBox className={styles.iconContainer}>
              {!item.isSold && !!item?.coupons?.length && <CouponIcon />}
              {item.saletype === 2 && <AuctionIcon />}
              {item?.size && <MantineText className={styles.size}>{item.size}</MantineText>}
            </MantineBox>
          </MantineBox>
          <MantineTitle order={3}>{item.title?.slice(0, 60)}</MantineTitle>
          <MantineBox className={styles.priceBar}>
            {item?.region && <MantineText className={styles.region}>{item.region}</MantineText>}
            <MantineText className={`${styles.price} ${isSold ? styles.sold : ""}`}>
              {isSold && "Sold"}
              <MantineText span fw={700}>
                ${item.priceusd} USD
              </MantineText>
              <Link
                href={`/search/product/${item.id}`}
                onClick={(e) => e.stopPropagation()}
                className={styles.share}
                aria-label="View item"
                target="_blank"
                prefetch={false}
              >
                <NewWindowIcon />
              </Link>
            </MantineText>
          </MantineBox>
          <MantineBox className={styles.stats}>
            <MantineAnchor
              className={styles.vendor}
              href={item.url}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => {
                e.stopPropagation();
                try {
                  trackItemOutboundClick(item, "selection_card");
                } catch {
                  // Analytics must never affect outbound navigation
                }
              }}
            >
              {item.vendor?.label}
            </MantineAnchor>
            {!!item?.vendor?.coupons?.length && (
              <MantineBox className={styles.coupons}>
                <MantineText span component="b">
                  Coupon:{" "}
                </MantineText>
                {item.vendor.coupons.map((coupon) => (
                  <MantineBox key={coupon.couponcode}>
                    <MantineText>
                      Code:{" "}
                      <MantineText span fw={700}>
                        {coupon.couponcode}
                      </MantineText>
                    </MantineText>
                    <MantineText>Details: {coupon.coupondescription}</MantineText>
                    <MantineText>
                      Expiry: {formatDateInTimezone(coupon.enddate, "America/Los_Angeles")}
                    </MantineText>
                  </MantineBox>
                ))}
              </MantineBox>
            )}
            {!!filteredProxies?.length && (
              <MantineBox className={styles.proxies}>
                <MantineText span component="b">
                  Proxies:{" "}
                </MantineText>
                {filteredProxies?.map((proxy) => (
                  <MantineAnchor
                    key={proxy.proxyname}
                    href={proxy.proxyurl}
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => {
                      sendGtagEvent(
                        "proxy_outbound",
                        "outbound",
                        `${proxy.proxyname} - ${item.vendor?.label ?? ""}`,
                        {
                          proxy: proxy.proxyname,
                          vendor: item.vendor?.label ?? "",
                          item_id: String(item.id),
                        }
                      );
                      try {
                        trackEvent("item_outbound_click", "engagement", {
                          item_id: item.id,
                          vendor: item.vendor?.label,
                          source: "selection_card_proxy",
                          proxy: proxy.proxyname,
                        });
                      } catch {
                        // Analytics must never affect outbound navigation
                      }
                    }}
                  >
                    {proxy.proxyname}
                  </MantineAnchor>
                ))}
              </MantineBox>
            )}
            {timestampDisplay && (
              <MantineText className={styles.timeSince}>{timestampDisplay}</MantineText>
            )}
          </MantineBox>
        </MantineBox>
      </MantineBox>
    </MantineBox>
  );
};

export default SelectionCard;
